import React from 'react';
import {
  Flex,
  Spinner,
  Text,
  Box,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
} from '@chakra-ui/react';
import {useGetSegments} from '../../../hooks/api/segments';
import {pluralize} from '../../../lib/helpers';

export const SegmentsPanel = ({responseId}: {responseId?: string}) => {
  if (!responseId) {
    return (
      <Box w="400px" borderLeft="1px" borderColor="gray.200" bg="gray.50" p={4}>
        <Text color="gray.500" textAlign="center" fontSize="sm">
          Waiting for initial response...
        </Text>
      </Box>
    );
  }

  return <SegmentsPanelContent responseId={responseId} />;
};

const SegmentsPanelContent = ({responseId}: {responseId: string}) => {
  const getSegments = useGetSegments(
    {previousResponseId: responseId},
    {
      // Don't refetch the segments if the data is still cached.
      staleTime: Infinity,
    }
  );

  if (getSegments.isLoading) {
    return (
      <Box
        w="400px"
        borderLeft="1px"
        borderColor="gray.200"
        bg="white"
        p={4}
        overflowY="auto"
      >
        <Flex justify="center" align="center" py={8}>
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text color="gray.600" fontSize="sm">
              Loading segments...
            </Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  if (getSegments.isError) {
    return (
      <Box
        w="400px"
        borderLeft="1px"
        borderColor="gray.200"
        bg="white"
        p={4}
        overflowY="auto"
      >
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Error loading segments</AlertTitle>
            <AlertDescription>
              Unable to load the generated segments. Please try again.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  const segments = getSegments.data?.segments ?? [];
  return (
    <Box
      w="400px"
      borderLeft="1px"
      borderColor="gray.200"
      bg="white"
      p={4}
      overflowY="auto"
    >
      <VStack spacing={6} align="stretch">
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            {pluralize(segments.length, 'Segment', 'Segments')} Generated
          </Text>
          <Text fontSize="sm" color="gray.600">
            These segments represent different customer groups identified by the
            AI analysis.
          </Text>
        </Box>

        <Divider />

        <VStack spacing={4} align="stretch">
          {segments.map((segment, index) => (
            <Box
              key={index}
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              p={4}
              shadow="sm"
              _hover={{shadow: 'md'}}
              transition="all 0.2s"
            >
              <VStack align="stretch" spacing={3}>
                {/* Segment Header */}
                <HStack justify="space-between" align="flex-start">
                  <VStack align="flex-start" spacing={1} flex={1}>
                    <HStack spacing={2}>
                      <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                        Segment {index + 1}
                      </Badge>
                      <Text
                        fontSize="md"
                        fontWeight="semibold"
                        color="gray.800"
                      >
                        {segment.name}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>

                {/* Segment Definition */}
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                    mb={1}
                  >
                    Definition
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    {segment.definition}
                  </Text>
                </Box>

                {/* Segment Examples */}
                {segment.examples && segment.examples.length > 0 && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.700"
                      mb={1}
                    >
                      Examples
                    </Text>
                    <Flex wrap="wrap" gap={1}>
                      {segment.examples.map((example, exampleIndex) => (
                        <Badge
                          key={exampleIndex}
                          colorScheme="green"
                          variant="outline"
                          fontSize="xs"
                          px={2}
                          py={1}
                        >
                          {example}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                )}
              </VStack>
            </Box>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};
